<ng-container *transloco="let t">
  <mat-stepper [linear]="isLinear" #stepper>
    <!-- Step 1: Select Veterinarian -->
    <mat-step [label]="t('Select veterinarian')">
      <mat-progress-bar mode="determinate" [value]="33"></mat-progress-bar>

      <div class="step-content">
        @if (store.status() === status.Loading) {
          <div class="dialog-loading-centered">
            <mat-progress-spinner
              diameter="24"
              mode="indeterminate"
              strokeWidth="3"
            ></mat-progress-spinner>
          </div>
        } @else if ((store.data()?.length ?? 0) > 0) {
          <p class="step-hint">{{ t('Select veterinarian') }}</p>
          <div class="vet-list">
            @for (veterinarian of store.data(); track veterinarian) {
              <div
                class="vet-row"
                (click)="setVeterinarian(veterinarian.uid!)"
                role="button"
                tabindex="0"
                (keydown.enter)="setVeterinarian(veterinarian.uid!)"
                (keydown.space)="setVeterinarian(veterinarian.uid!)"
              >
                <div class="vet-row__avatar">
                  @if (veterinarian?.profilePicture) {
                    <img
                      [src]="veterinarian?.profilePicture?.thumbnailUrl"
                      class="vet-avatar-img"
                      alt=""
                    />
                  } @else {
                    <anihis-name-initials
                      [firstName]="veterinarian?.firstName"
                      [lastName]="veterinarian?.lastName"
                      [gender]="veterinarian?.gender"
                    ></anihis-name-initials>
                  }
                </div>
                <div class="vet-row__info">
                  <span class="vet-row__name"
                    >{{ veterinarian.firstName }}
                    {{ veterinarian.lastName }}</span
                  >
                  <span class="vet-row__email">{{ veterinarian.email }}</span>
                </div>
                <mat-icon class="vet-row__arrow">chevron_right</mat-icon>
              </div>
            }
          </div>
        } @else {
          <div class="empty-state">
            <p class="empty-state__text">
              {{ t('validation.message.noVeterinaryInClinic') }}
            </p>
          </div>
        }
      </div>

      <mat-dialog-actions align="end">
        <button
          mat-stroked-button
          class="secondary-btn"
          (click)="closeDialog()"
        >
          {{ t('Close') }}
        </button>
      </mat-dialog-actions>
    </mat-step>

    <!-- Step 2: Veterinarian PIN -->
    <mat-step [stepControl]="vetPinForm">
      <mat-progress-bar mode="determinate" [value]="66"></mat-progress-bar>
      <ng-template matStepLabel>{{ t('Veterinarian PIN') }}</ng-template>

      <form [formGroup]="vetPinForm" class="step-content">
        <div class="pin-container">
          <div class="pin-icon-wrap">
            <div class="pin-icon-inner">
              <img class="key-icon" src="/assets/icons/key.svg" alt="" />
            </div>
          </div>
          <p class="pin-title">{{ t('Enter veterinarian pin') }}</p>
          <p class="pin-subtitle">
            {{ t('Enter the 4-digit PIN to confirm identity') }}
          </p>

          <mat-form-field appearance="outline" class="pin-field input-border">
            <mat-label>{{ t('PIN') }}</mat-label>
            <input
              appAutofocus
              matInput
              formControlName="vetPin"
              type="password"
              minlength="4"
              maxlength="4"
              pattern="\d{4}"
            />
            @if (
              vetPinForm.controls.vetPin.hasError('incorrect') ||
              vetPinForm.controls.vetPin.hasError('serverError')
            ) {
              <mat-error>{{ t(errorMessage ?? '') }}</mat-error>
            }
          </mat-form-field>

          <button
            mat-raised-button
            (click)="submitPin()"
            class="primary-btn pin-submit-btn"
            [disabled]="
              vetPinForm.invalid || store.submitStatus() === status.Loading
            "
          >
            @if (store.submitStatus() === status.Loading) {
              <mat-progress-spinner
                diameter="18"
                mode="indeterminate"
                strokeWidth="3"
              ></mat-progress-spinner>
              <span>{{ t('Verifying...') }}</span>
            } @else {
              <span>{{ t('Submit PIN') }}</span>
            }
          </button>
        </div>
      </form>

      <mat-dialog-actions align="end">
        <button mat-stroked-button matStepperPrevious class="secondary-btn">
          {{ t('Back step') }}
        </button>
      </mat-dialog-actions>
    </mat-step>

    <!-- Step 3: Upload File -->
    <mat-step [label]="t('Upload File')">
      <mat-progress-bar mode="determinate" [value]="100"></mat-progress-bar>

      <div class="step-content">
        <ngx-file-drop
          class="drop-zone"
          (onFileDrop)="dropped($event)"
          [multiple]="true"
          accept=".jpg,.jpeg,.png,.pdf,.docx,.txt"
        >
          <ng-template
            ngx-file-drop-content-tmp
            let-openFileSelector="openFileSelector"
          >
            <div class="drop-zone__inner">
              <div class="drop-zone__icon-wrap">
                <div class="drop-zone__icon-inner">
                  <mat-icon class="upload-icon"></mat-icon>
                </div>
              </div>
              <div class="drop-zone__text">
                <span
                  class="drop-zone__link"
                  (click)="openFileSelector()"
                  role="button"
                  tabindex="0"
                  (keydown.enter)="openFileSelector()"
                  (keydown.space)="openFileSelector()"
                  >{{ t('Click to upload') }}</span
                >
                {{ t('or drag and drop') }}
              </div>
              <p class="drop-zone__hint">PDF, DOCX, JPG, PNG</p>
            </div>
          </ng-template>
        </ngx-file-drop>

        @if (files.length) {
          <div class="file-list">
            @for (file of files; track file) {
              <div
                class="file-item"
                [class.file-item--success]="currentStatus === status.Loaded"
              >
                <div class="file-item__icon">
                  <div class="file-item__icon-inner">
                    <mat-icon class="file-blue-icon"></mat-icon>
                  </div>
                </div>
                <div class="file-item__info">
                  <span class="file-item__name">{{
                    file.name | limitTo: 35
                  }}</span>
                  <span class="file-item__size">{{ file.size }}</span>
                  <mat-form-field
                    appearance="outline"
                    class="document-name-field"
                  >
                    <mat-label>{{ t('Document name / purpose') }}</mat-label>
                    <input matInput [(ngModel)]="file.documentName" />
                  </mat-form-field>
                  @if (currentStatus === status.Loaded) {
                    <div class="file-item__progress">
                      <span class="file-item__progress-bar"></span>
                      <span class="file-item__progress-label">100%</span>
                    </div>
                  }
                </div>
                <button
                  mat-icon-button
                  class="file-item__remove"
                  (click)="deleteFile(file)"
                  aria-label="Icon button"
                >
                  <mat-icon class="remove-icon"></mat-icon>
                </button>
              </div>
            }
          </div>
        }

        @if (!files.length && submitted) {
          <mat-error class="margin-top-xs">{{
            t('File is required')
          }}</mat-error>
        }
      </div>

      <mat-dialog-actions align="end">
        <button mat-stroked-button matStepperPrevious class="secondary-btn">
          {{ t('Back') }}
        </button>
        <button
          mat-raised-button
          class="primary-btn"
          (click)="saveChanges()"
          [disabled]="
            isSaving() || files.length === 0 || currentStatus === status.Loading
          "
        >
          @if (isSaving()) {
            <mat-progress-spinner
              diameter="18"
              mode="indeterminate"
              strokeWidth="3"
            ></mat-progress-spinner>
            <span>{{ t('Saving...') }}</span>
          } @else {
            <span>{{ t('Done') }}</span>
          }
        </button>
      </mat-dialog-actions>
    </mat-step>
  </mat-stepper>
</ng-container>
